import { defineRowEntry, type AnyRowEntry } from './registry';

import { ContentSuggestionBubble } from '@/components/chat/bubbles/ContentSuggestionBubble';
import { PrimumSupportMessageBubble } from '@/components/chat/bubbles/PrimumSupportMessageBubble';
import { isRecipientContent } from '@/hooks/chat/useMessageLinkOpener';
import { MESSAGE_DATA_TYPES } from '@/types/messages/discriminators';
import { isSingleAudienceSupportMessage } from '@/types/messages/supportMessage';
import { isAnonymousPrimumRoutedMessage } from '@/util/inquiryDisplay';

/**
 * The ordered bubble registry.
 *
 * Order is behavior, not style. Entries are tried top-down and the first match
 * wins, so any entry whose `match` is broader than a plain type check has to
 * sit above the entries it could shadow.
 */

/**
 * Claims every kind of Primum Support message: the app's own composer sends
 * (`primum_support`), the backend's audience-addressed ones
 * (`support_message`), and anonymous-relay messages that carry no
 * discriminator of their own. That last predicate is broader than a type match
 * and can claim a message another entry would otherwise take, so this entry
 * must stay first.
 *
 * All three share one bubble deliberately — support copy reads as the same
 * thing wherever it came from, and routing them here is what keeps the avatar,
 * the "Primum Support" label, and the bordered card from drifting per producer.
 *
 * Which viewers see an audience-addressed message at all is decided upstream in
 * `useChatMessages`, not by this entry's `visibility`: dropping it from the list
 * rather than hiding it at render is what keeps the message out of a length the
 * expert's first-message prompt depends on.
 */
const primumSupportEntry = defineRowEntry({
  type: MESSAGE_DATA_TYPES.PRIMUM_SUPPORT,
  match: (message, ctx) =>
    message?.type === MESSAGE_DATA_TYPES.PRIMUM_SUPPORT ||
    message?.type === MESSAGE_DATA_TYPES.SUPPORT_MESSAGE ||
    isAnonymousPrimumRoutedMessage(message, {
      inquiry: ctx.inquiry,
      currentUserId: ctx.userId,
      routingUserId: ctx.inquiry.recipient.id,
    }),
  visibility: 'both',
  container: {
    kind: 'row',
    position: (_message, ctx) => (ctx.isPrimumAskAnonymouslyInboxConversation ? 'right' : 'left'),
  },
  errorBoundary: {
    variant: 'primum-support',
    // This bubble is only ActionBubble + Text + a string, so a fallback rebuilt
    // from those same primitives would share its entire failure surface and
    // throw again — escaping the boundary. The ordinary message row renders the
    // same text through a different path, so it is the one fallback that does
    // not depend on whatever just broke.
    fallback: 'default',
  },
  toProps: (message, ctx) => ({
    text: message.text,
    alignEnd: ctx.isPrimumAskAnonymouslyInboxConversation,
    showVisibilityBadge: isSingleAudienceSupportMessage(message),
    conversationId: ctx.conversation.id,
    messageSid: message.messageId,
    linksAreReferences: isRecipientContent(message, ctx.inquiry.recipient.id),
  }),
  Component: PrimumSupportMessageBubble,
});

/**
 * Submitter-only "while you wait" nudge. The expert shares the same
 * conversation and must not see it at all, hence the visibility gate rather
 * than an authorship check — the message is system-authored.
 */
const contentSuggestionEntry = defineRowEntry({
  type: MESSAGE_DATA_TYPES.CONTENT_SUGGESTION,
  visibility: 'submitter',
  container: { kind: 'row' },
  // A passive suggestion card: nothing is lost by it not appearing, and the
  // message it decorates has no text of its own to fall back to.
  errorBoundary: { variant: 'content-suggestion', fallback: null },
  toProps: (message, ctx) => {
    if (!message.contentCard) {
      return null;
    }

    return {
      contentCard: message.contentCard,
      conversationId: ctx.conversation.id,
      messageSid: message.messageId,
    };
  },
  Component: ContentSuggestionBubble,
});

/**
 * Entries that replace the whole message row, tried top-down.
 *
 * Order is behavior, not style. Primum Support's `match` is broader than a type
 * check and can claim a message another entry would otherwise take, so it stays
 * first.
 */
export const ROW_ENTRIES: readonly AnyRowEntry[] = [primumSupportEntry, contentSuggestionEntry];
